import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Inbox as InboxIcon, Send, Bot, HandMetal, Instagram, MessageCircle, ExternalLink, Search, AlertCircle, Clock, X,
  Archive, ArchiveRestore, Ban, ShieldCheck, Trash2, Filter, MoreVertical, PanelRightOpen, PanelRightClose, ChevronDown, UserRound, ChevronLeft,
  MapPin, ListOrdered,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { hasFeature, clienteTiene } from '../utils/features';
import { buildWhatsAppWebUrl, STATUS_CONFIG, QUALIFICATION_CONFIG, estadosPara } from '../utils/constants';
import AiAnalysis from '../components/AiAnalysis';
import UsageMeters from '../components/UsageMeters';
import LeadDetail from '../components/LeadDetail';
import EtiquetasLead from '../components/EtiquetasLead';

/* Quién está atendiendo, para verlo en la lista sin abrir la conversación.

   El cartel de Bot/Humano/Derivado existía solo arriba del chat, así que una
   conversación derivada a un asesor no se distinguía de una que atiende el bot
   hasta que la abrías. Y encima una derivada queda fuera del cálculo de
   atención, así que tampoco tenía borde de color: era la menos visible de
   todas, justo la que alguien tiene que seguir.

   Va a la izquierda, separado del aviso de atención que va a la derecha: son
   dos cosas distintas —quién la atiende y qué le falta— y competir por el mismo
   lugar las hacía tapar una a la otra. El bot no se muestra: es lo normal y
   marcarlo sería ruido en casi todas las filas. */
const atiende = (c) => {
  if (c.blocked || c.state === 'closed') return null;
  // cada uno se explica solo: el texto viaja con la etiqueta y no en un if aparte
  if (c.status === 'contactado_por_fuera') {
    return { label: 'Por fuera', color: '#0d9488', ayuda: 'Se lo contactó desde otro número: la conversación sigue fuera del CRM.' };
  }
  if (c.status === 'derivado_asesor' || c.state === 'derived') {
    return { label: 'Derivado', color: '#0ea5e9', ayuda: 'Derivada a un asesor: la sigue una persona.' };
  }
  if (c.state === 'human') {
    return { label: 'Humano', color: '#f97316', ayuda: 'La está atendiendo una persona desde el CRM.' };
  }
  return null;
};

const stateBadge = (s) => {
  if (s === 'human') return { label: 'Humano', bg: 'rgba(249,115,22,0.15)', color: '#f97316' };
  if (s === 'derived') return { label: 'Derivado', bg: 'rgba(14,165,233,0.15)', color: '#0ea5e9' };
  if (s === 'closed') return { label: 'Archivada', bg: 'rgba(100,116,139,0.15)', color: '#64748b' };
  return { label: 'Bot', bg: 'var(--accent-glow)', color: 'var(--accent)' };
};
const channelLabel = (c) => c === 'instagram' ? 'Instagram' : c === 'whatsapp' ? 'WhatsApp' : (c || '');
const ChannelIcon = ({ c, size = 14 }) => c === 'instagram'
  ? <Instagram size={size} color="#E1306C" />
  : c === 'whatsapp' ? <MessageCircle size={size} color="#25D366" /> : null;
const parseDate = (d) => {
  if (!d) return null;
  let dt = new Date(d);
  if (isNaN(dt.getTime())) dt = new Date(String(d).replace(' ', 'T') + 'Z');
  return isNaN(dt.getTime()) ? null : dt;
};
const fmtTime = (d) => {
  const dt = parseDate(d);
  return dt ? dt.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
};
// Hora si es de hoy, fecha corta si no (para la lista)
const fmtListTime = (d) => {
  const dt = parseDate(d);
  if (!dt) return '';
  return dt.toDateString() === new Date().toDateString()
    ? dt.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })
    : dt.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
};
const daysLabel = (n) => n <= 0 ? 'hoy' : n === 1 ? '1 día' : `${n} días`;

/* Qué mandó la persona, para la vista previa de la lista.

   Un audio transcripto y una foto con texto se guardan como texto común: sin la
   marca, en la lista parecen mensajes escritos y el "[foto]" a secas no dice
   nada. La marca se pone acá y no en la base porque ese mismo texto es el que
   lee el bot como mensaje del cliente. */
const ICONO = {
  audio: '🎤', foto: '📷', imagen: '📷', video: '🎬', sticker: '🏷️',
  pdf: '📄', documento: '📄', archivo: '📎', contacto: '👤', ubicación: '📍',
  publicación: '🔗', historia: '⭕', mensaje: '💬',
};
const NOMBRE = {
  foto: 'Foto', imagen: 'Foto', video: 'Video', sticker: 'Sticker', pdf: 'PDF',
  documento: 'Documento', archivo: 'Archivo', contacto: 'Contacto',
  ubicación: 'Ubicación', publicación: 'Publicación', historia: 'Historia', mensaje: 'Mensaje',
};
const resumenUltimo = (tipo, body) => {
  const texto = String(body || '');
  if (!tipo || tipo === 'text') return texto;
  if (tipo === 'audio') return texto && texto !== '[audio]' ? '🎤 Audio: ' + texto : '🎤 Audio (sin transcribir)';
  const icono = ICONO[tipo] || '📎';
  // si vino con un texto al lado, ese texto es lo que importa
  const propio = texto && !/^\[.*\]$/.test(texto);
  return `${icono} ${propio ? texto : (NOMBRE[tipo] || tipo)}`;
};

// Nombre "real" del lead (si es un teléfono o un id, no lo usamos en el saludo)
const leadFirstName = (name) => {
  if (!name) return '';
  const n = String(name).replace(/^@/, '').trim();
  return /^[+\d\s()-]+$/.test(n) ? '' : n;
};

/* "Fuera del bot": se le mandó el seguimiento, no contestó y se cerró la ventana
   de 24 h. Desde ahí WhatsApp ya no entrega nada desde el CRM, así que si se lo
   quiere recuperar hay que escribirle por afuera. Se calcula, no se guarda: así
   no queda un estado viejo cuando el cliente vuelve a escribir. */
const fueraDelBot = (c) => !!c.seguimiento_at && c.last_dir === 'out'
  && c.last_age_min != null && Number(c.last_age_min) > 24 * 60;

const MAIN_VIEWS = [
  { key: 'all', label: 'Todas' },
  { key: 'reply', label: 'Sin responder', color: '#ef4444' },
  { key: 'followup', label: 'Seguimiento', color: '#f59e0b' },
];
const MORE_VIEWS = [
  { key: 'derived', label: 'Derivadas a asesor' },
  { key: 'archived', label: 'Archivadas' },
  { key: 'blocked', label: 'Bloqueadas' },
];

const EMPTY_FILTERS = { channel: '', status: '', qualification: '', attended: '' };
const FILTER_LABELS = {
  channel: (v) => channelLabel(v),
  status: (v) => STATUS_CONFIG[v]?.label || v,
  qualification: (v) => QUALIFICATION_CONFIG[v]?.label || v,
  attended: (v) => ({ bot: 'Atiende el bot', human: 'Atiende un humano', derived: 'Derivado' }[v] || v),
};

const pill = (bg, color) => ({ background: bg, color, border: 'none', borderRadius: 20, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', width: '100%' });
const menuBox = { position: 'absolute', zIndex: 20, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.25)', padding: 6 };
const menuItem = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, width: '100%', padding: '8px 10px', background: 'transparent', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)', textAlign: 'left' };
const sectionTitle = { fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 };

// true en pantallas chicas (mobile): lista y chat se muestran de a uno
function useIsMobile(bp = 768) {
  const q = `(max-width: ${bp}px)`;
  const [m, setM] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = (e) => setM(e.matches);
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on); };
  }, [q]);
  return m;
}

const readPref = () => { try { return localStorage.getItem('inbox_details') !== '0'; } catch { return true; } };

export default function Inbox() {
  const { tenant, user } = useAuth();
  const [view, setView] = useState('all');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [menu, setMenu] = useState(null); // 'filters' | 'more' | 'actions'
  const isMobile = useIsMobile();
  const [showDetails, setShowDetails] = useState(() => !window.matchMedia('(max-width: 768px)').matches && readPref());
  const [leadOpen, setLeadOpen] = useState(false);
  const [usage, setUsage] = useState(null);
  const [list, setList] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selId, setSelId] = useState(() => parseInt(searchParams.get('c'), 10) || null);
  useEffect(() => { const c = parseInt(searchParams.get('c'), 10); if (c) { setSelId(c); setSearchParams({}, { replace: true }); } }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps
  const [detail, setDetail] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [ubicacion, setUbicacion] = useState('');
  const [hayListas, setHayListas] = useState({ minorista: false, mayorista: false });
  const bottomRef = useRef(null);
  const textoRef = useRef(null);
  // Hechas para Punto Reciclaje: en los demás CRM no aparecen (ni para el super admin)
  const conEtiquetas = clienteTiene(tenant, 'etiquetas_charla');
  const conListas = clienteTiene(tenant, 'listas_precios');
  const conUbicacion = clienteTiene(tenant, 'ubicacion_lead');

  const loadList = useCallback(() => { api.get('/conversations').then(r => setList(r.data)).catch(() => {}); }, []);
  const loadDetail = useCallback((id) => { if (!id) return; api.get('/conversations/' + id).then(r => setDetail(r.data)).catch(() => {}); }, []);

  useEffect(() => { loadList(); const t = setInterval(loadList, 8000); return () => clearInterval(t); }, [loadList]);
  useEffect(() => { setDetail(null); loadDetail(selId); const t = setInterval(() => loadDetail(selId), 8000); return () => clearInterval(t); }, [selId, loadDetail]);
  useEffect(() => { if (bottomRef.current) bottomRef.current.scrollIntoView(); }, [detail?.messages?.length]);

  const refreshAll = () => { loadDetail(selId); loadList(); };
  // Qué listas de precios cargó el negocio: sin lista, su botón no aparece
  useEffect(() => { if (!conListas) return; api.get('/conversations/listas-precios').then(r => setHayListas({ minorista: !!r.data.minorista.trim(), mayorista: !!r.data.mayorista.trim() })).catch(() => {}); }, [conListas]);
  const toggleDetails = () => { const v = !showDetails; setShowDetails(v); if (!isMobile) { try { localStorage.setItem('inbox_details', v ? '1' : '0'); } catch {} } };
  const chatOpen = isMobile && !!selId; // en mobile: con conversación abierta se oculta la lista

  // Búsqueda + filtros; después la vista (así los contadores respetan los filtros)
  const baseList = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter(c =>
      (!filters.channel || c.channel === filters.channel) &&
      (!filters.status || c.status === filters.status) &&
      (!filters.qualification || c.qualification === filters.qualification) &&
      (!filters.attended || c.state === filters.attended) &&
      (!q || String(c.business_name || '').toLowerCase().includes(q) || String(c.phone || c.external_id || '').includes(q))
    );
  }, [list, filters, search]);
  const counts = useMemo(() => {
    const open = baseList.filter(c => c.state !== 'closed' && !c.blocked);
    return {
      all: open.length,
      reply: open.filter(c => c.needs_reply).length,
      followup: open.filter(c => c.needs_followup).length,
      derived: open.filter(c => c.state === 'derived').length,
      archived: baseList.filter(c => c.state === 'closed' && !c.blocked).length,
      blocked: baseList.filter(c => c.blocked).length,
    };
  }, [baseList]);
  const shown = baseList.filter(c => view === 'blocked' ? !!c.blocked : c.blocked ? false : view === 'archived' ? c.state === 'closed' : c.state !== 'closed' && (
    view === 'reply' ? c.needs_reply : view === 'followup' ? c.needs_followup : view === 'derived' ? c.state === 'derived' : true));
  const activeFilters = Object.entries(filters).filter(([, v]) => v);
  const moreActive = MORE_VIEWS.find(v => v.key === view);

  const send = async () => {
    if (!text.trim() || !selId) return;
    setSending(true);
    try { await api.post('/conversations/' + selId + '/reply', { text }); setText(''); refreshAll(); }
    catch (e) { toast.error(e.response?.data?.error || 'Error al enviar'); }
    finally { setSending(false); }
  };
  const changeState = async (state) => {
    setMenu(null);
    try {
      await api.put('/conversations/' + selId + '/state', { state });
      if (state === 'closed') { setSelId(null); toast.success('Conversación archivada. Si el cliente vuelve a escribir, se reabre sola.'); }
      else toast.success(state === 'bot' ? 'El bot retoma la conversación' : state === 'derived' ? 'Conversación restaurada' : 'Tomaste la conversación');
      refreshAll();
    } catch { toast.error('Error'); }
  };
  const conv = detail?.conversation;
  // La ubicación se edita en un campo propio: se resetea solo al cambiar de lead, no en cada refresco de 8 s
  useEffect(() => { setUbicacion(conv?.city || ''); }, [conv?.lead_id, conv?.city]);
  const guardarUbicacion = async () => {
    if (!conv?.lead_id || ubicacion.trim() === (conv.city || '')) return;
    try { await api.put(`/leads/${conv.lead_id}/ubicacion`, { city: ubicacion }); refreshAll(); toast.success('Ubicación guardada'); }
    catch { toast.error('No se pudo guardar la ubicación'); }
  };
  /* Pega la lista de precios en el campo de respuesta, sin mandarla: la persona
     la revisa y la ajusta antes de enviar. Se pide recién ahora para que salga
     la versión que está guardada en el Agente IA. */
  const pegarLista = async (cual) => {
    try {
      const { data } = await api.get('/conversations/listas-precios');
      const lista = (data[cual] || '').trim();
      if (!lista) return toast.error('Esa lista está vacía: se carga en Agente IA');
      setText(t => (t.trim() ? t.replace(/\s+$/, '') + '\n\n' : '') + lista);
      setTimeout(() => { const el = textoRef.current; if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); } }, 0);
    } catch { toast.error('No se pudo traer la lista de precios'); }
  };
  const removeConversation = async (withLead) => {
    setMenu(null);
    const msg = withLead
      ? '¿Borrar la conversación Y el lead? Se eliminan los mensajes, la ficha del contacto, sus notas y su historial. No se puede deshacer.'
      : '¿Borrar esta conversación? Se eliminan todos los mensajes y el análisis. El lead se conserva. Si el contacto vuelve a escribir, empieza una conversación nueva y limpia.';
    if (!window.confirm(msg)) return;
    try {
      await api.delete('/conversations/' + selId + (withLead ? '?with_lead=1' : ''));
      toast.success(withLead ? 'Conversación y lead eliminados' : 'Conversación eliminada');
      setSelId(null); setDetail(null); loadList();
    } catch { toast.error('No se pudo eliminar'); }
  };
  const setBlocked = async (blocked) => {
    setMenu(null);
    if (blocked && !window.confirm('¿Bloquear este contacto? Sus mensajes se van a ignorar y el bot no le va a responder.')) return;
    try { await api.put('/conversations/' + selId + '/block', { blocked }); toast.success(blocked ? 'Contacto bloqueado' : 'Contacto desbloqueado'); refreshAll(); }
    catch { toast.error('Error'); }
  };
  const updateLead = async (field, value) => {
    if (!conv?.lead_id) return;
    try {
      await api.put(`/leads/${conv.lead_id}/${field}`, { [field]: value });
      refreshAll();
      if (field === 'status' && value === 'derivado_asesor') toast.success('Derivado a asesor: el bot no va a responder esta conversación');
      else if (field === 'status' && conv.status === 'derivado_asesor') toast.success('El bot retoma la conversación');
    } catch { toast.error('No se pudo actualizar'); }
  };

  const canAnalysis = hasFeature(tenant, user, 'ai_analysis');
  const waPhone = conv ? (conv.phone || (conv.channel === 'whatsapp' ? conv.external_id : null)) : null;
  const outside24h = conv && conv.last_in_age_min != null && Number(conv.last_in_age_min) > 24 * 60;
  const canWaWeb = conv && waPhone && hasFeature(tenant, user, 'wa_web') && (conv.state !== 'bot' || outside24h);
  const waBlocked = conv?.channel === 'whatsapp' && usage?.whatsapp?.blocked;
  /* Fuera de las 24 h WhatsApp no entrega un mensaje libre, y Twilio no lo
     rechaza en el momento: lo acepta y falla después. El aviso ya estaba, pero
     era solo un texto y el botón seguía activo, así que se siguió escribiendo
     igual y los mensajes no llegaban. Ahora frena de verdad. */
  const sinVentana = conv?.channel === 'whatsapp' && outside24h;
  const openWaWeb = async () => {
    /* La presentación va armada con quién sos y dónde trabajás, no fija: el
       mismo botón lo usan los CRM de todos los clientes, y "Emanuel de uDream"
       no sirve para el asesor de otro negocio.

       Termina ahí a propósito: el mensaje abre la puerta y la conversación la
       seguís escribiendo vos. */
    const name = leadFirstName(conv.business_name);
    const empresa = tenant?.company_name || tenant?.name || '';
    const yo = String(user?.display_name || '').trim().split(/\s+/)[0];
    const msg = `Hola${name ? ' ' + name : ''}! ¿Cómo estás?`
      + (yo ? ` Mi nombre es ${yo}` : ' Te escribo')
      + (empresa ? ` de ${empresa}.` : '.');
    window.open(buildWhatsAppWebUrl(waPhone, msg), '_blank', 'noopener');
    /* Queda registrado que la charla siguió por fuera del CRM.

       Antes se marcaba como "derivado a asesor", que es otra cosa: derivar es
       pasarle el lead a otro, y acá el que escribió fue uno mismo. Se puede
       cambiar a mano después, como cualquier estado. */
    const YA_AVANZADO = ['propuesta_enviada', 'ganado', 'perdido'];
    if (conv.status !== 'contactado_por_fuera' && !YA_AVANZADO.includes(conv.status)) {
      await updateLead('status', 'contactado_por_fuera');
    }
  };

  // Un solo aviso a la vez, el más importante
  const notice = !conv ? null
    : conv.blocked ? { color: 'var(--danger)', text: 'Contacto bloqueado: sus mensajes se ignoran y el bot no responde.' }
    : conv.spam_flag ? { color: '#d97706', text: 'Bot pausado por actividad inusual' + (conv.flag_reason ? ': ' + conv.flag_reason : '') + '. Si es spam, bloqueá el contacto; si no, devolvelo al bot.' }
    : waBlocked ? { color: 'var(--danger)', text: 'Llegaste al tope de WhatsApp: no se pueden enviar mensajes por este canal. ' + (usage?.limit_message || '') }
    : conv.state === 'closed' ? { color: 'var(--text-muted)', text: 'Archivada. Si el cliente vuelve a escribir, se reabre sola.' }
    : conv.state === 'derived' ? { color: '#0ea5e9', text: 'Derivado a un asesor: sigue por fuera y el bot no responde.' }
    : outside24h ? { color: '#d97706', text: conv.channel === 'whatsapp' ? 'Pasaron más de 24 h desde el último mensaje del cliente. WhatsApp no entrega mensajes libres fuera de esa ventana: aunque acá dijera "enviado", no llegaría. Escribile desde WhatsApp Web con el botón de arriba.' : 'Pasaron más de 24 h desde el último mensaje del cliente: Instagram puede rechazar el envío.' }
    : conv.state === 'bot' ? { color: 'var(--text-muted)', text: 'Responde el bot. Si escribís, tomás la conversación.' }
    : null;

  const sc = conv ? (STATUS_CONFIG[conv.status] || {}) : {};
  const qc = conv ? (QUALIFICATION_CONFIG[conv.qualification] || {}) : {};

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: isMobile ? 'calc(100dvh - 72px)' : 'calc(100vh - 56px)', minHeight: isMobile ? 380 : 520 }}>
      <div style={{ display: chatOpen ? 'none' : 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <InboxIcon size={22} color="var(--accent)" />
        <h1 style={{ fontSize: 22, fontWeight: 800 }}>Conversaciones</h1>
      </div>

      {usage?.demo_mode?.active && !chatOpen && (
        <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 8, fontSize: 13, background: 'rgba(168,85,247,0.12)', color: '#a855f7' }}>
          {usage.demo_mode.role === 'target'
            ? `Modo demo activo hasta las ${new Date(usage.demo_mode.until).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}: los mensajes al WhatsApp e Instagram de uDream entran acá.`
            : `Modo demo activo hasta las ${new Date(usage.demo_mode.until).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}: los mensajes nuevos están entrando al CRM demo, no a este.`}
        </div>
      )}

      {menu && <div onClick={() => setMenu(null)} style={{ position: 'fixed', inset: 0, zIndex: 15 }} />}

      <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', flex: 1, minHeight: 0, position: 'relative' }}>
        {/* ===== Lista ===== */}
        <div style={{ width: isMobile ? '100%' : 330, borderRight: isMobile ? 'none' : '1px solid var(--border)', display: chatOpen ? 'none' : 'flex', flexDirection: 'column', flexShrink: 0, background: 'var(--bg-secondary)' }}>
          <div style={{ padding: 10, borderBottom: '1px solid var(--border)', position: 'relative' }}>
            {/* Buscar + filtros */}
            <div style={{ display: 'flex', gap: 6 }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--text-muted)' }} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar" style={{ paddingLeft: 30, fontSize: 13 }} />
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => setMenu(menu === 'filters' ? null : 'filters')} title="Filtros"
                style={{ position: 'relative', zIndex: 16, gap: 4, borderColor: activeFilters.length ? 'var(--accent)' : undefined }}>
                <Filter size={14} />
                {activeFilters.length > 0 && <span style={{ fontSize: 10, fontWeight: 700, background: 'var(--accent)', color: '#fff', borderRadius: 10, padding: '0 6px' }}>{activeFilters.length}</span>}
              </button>
            </div>
            {menu === 'filters' && (
              <div style={{ ...menuBox, left: 10, right: 10, top: 52, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <select value={filters.channel} onChange={e => setFilters({ ...filters, channel: e.target.value })} style={{ fontSize: 13 }}>
                  <option value="">Todas las redes</option><option value="whatsapp">WhatsApp</option><option value="instagram">Instagram</option>
                </select>
                <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} style={{ fontSize: 13 }}>
                  <option value="">Todos los estados</option>
                  {estadosPara(null).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <select value={filters.qualification} onChange={e => setFilters({ ...filters, qualification: e.target.value })} style={{ fontSize: 13 }}>
                  <option value="">Todas las calificaciones</option>
                  {Object.entries(QUALIFICATION_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                </select>
                <select value={filters.attended} onChange={e => setFilters({ ...filters, attended: e.target.value })} style={{ fontSize: 13 }}>
                  <option value="">Atiende: todos</option><option value="bot">Bot</option><option value="human">Humano</option><option value="derived">Derivado</option>
                </select>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setFilters(EMPTY_FILTERS)} disabled={!activeFilters.length}>Limpiar</button>
                  <button className="btn btn-primary btn-sm" onClick={() => setMenu(null)}>Listo</button>
                </div>
              </div>
            )}
            {/* Filtros activos */}
            {activeFilters.length > 0 && (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 8 }}>
                {activeFilters.map(([k, v]) => (
                  <span key={k} className="badge" style={{ fontSize: 11, background: 'var(--accent-glow)', color: 'var(--accent)', gap: 4 }}>
                    {FILTER_LABELS[k](v)}
                    <button onClick={() => setFilters({ ...filters, [k]: '' })} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit', display: 'flex' }}><X size={11} /></button>
                  </span>
                ))}
              </div>
            )}
            {/* Vistas */}
            <div style={{ display: 'flex', gap: 2, marginTop: 10, position: 'relative' }}>
              {MAIN_VIEWS.map(v => {
                const active = view === v.key;
                const n = counts[v.key];
                return (
                  <button key={v.key} onClick={() => setView(v.key)}
                    style={{ flex: 'none', background: 'none', border: 'none', borderBottom: '2px solid ' + (active ? 'var(--accent)' : 'transparent'), padding: '4px 6px 6px', cursor: 'pointer', fontSize: 12, fontWeight: active ? 700 : 500, color: active ? 'var(--text-primary)' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    {v.label}
                    {v.key !== 'all' && n > 0 && <span style={{ fontSize: 10, fontWeight: 700, background: v.color, color: '#fff', borderRadius: 9, padding: '0 5px' }}>{n}</span>}
                  </button>
                );
              })}
              <button onClick={() => setMenu(menu === 'more' ? null : 'more')}
                style={{ position: 'relative', zIndex: 16, marginLeft: 'auto', background: 'none', border: 'none', borderBottom: '2px solid ' + (moreActive ? 'var(--accent)' : 'transparent'), padding: '4px 4px 6px', cursor: 'pointer', fontSize: 12, fontWeight: moreActive ? 700 : 500, color: moreActive ? 'var(--text-primary)' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 2 }}>
                {moreActive ? moreActive.label.split(' ')[0] : 'Más'} <ChevronDown size={12} />
              </button>
              {menu === 'more' && (
                <div style={{ ...menuBox, right: 0, top: 30, minWidth: 200 }}>
                  {MORE_VIEWS.map(v => (
                    <button key={v.key} style={{ ...menuItem, fontWeight: view === v.key ? 700 : 400 }} onClick={() => { setView(v.key); setMenu(null); }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{v.key === 'archived' && <Archive size={13} />}{v.label}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{counts[v.key]}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={{ overflowY: 'auto', flex: 1 }}>
            {shown.length === 0 ? (
              <div style={{ padding: 24, color: 'var(--text-muted)', fontSize: 13 }}>
                {list.length === 0 ? 'Todavía no hay conversaciones. Cuando alguien escriba por WhatsApp o Instagram, aparecen acá.'
                  : view === 'blocked' ? 'No hay contactos bloqueados.' : view === 'archived' ? 'No hay conversaciones archivadas.' : view === 'reply' ? '¡Todo respondido! 🎉' : view === 'followup' ? 'No hay conversaciones para hacer seguimiento.' : 'No hay conversaciones con estos filtros.'}
              </div>
            ) : shown.map(c => {
              const q = QUALIFICATION_CONFIG[c.qualification];
              const quien = atiende(c);
              const flag = c.blocked ? { color: 'var(--danger)', icon: Ban, text: 'Bloqueado' } : c.spam_flag ? { color: '#d97706', icon: AlertCircle, text: 'Posible spam' } : c.needs_reply ? { color: '#ef4444', icon: AlertCircle, text: 'Sin responder' } : c.needs_followup ? { color: '#f59e0b', icon: Clock, text: daysLabel(c.days_since_last) } : null;
              return (
                <div key={c.id} onClick={() => setSelId(c.id)} style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', cursor: 'pointer', background: selId === c.id ? 'var(--bg-hover)' : 'transparent', borderLeft: '3px solid ' + (flag ? flag.color : quien ? quien.color : 'transparent') }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
                      <ChannelIcon c={c.channel} size={14} />
                      <span style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.business_name || c.external_id}</span>
                      {q && <span title={q.label} style={{ fontSize: 11 }}>{q.icon}</span>}
                      {quien && (
                        <span title={quien.ayuda}
                          style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase',
                            color: quien.color, border: '1px solid ' + quien.color, borderRadius: 999,
                            padding: '1px 6px', flexShrink: 0, lineHeight: 1.5 }}>{quien.label}</span>
                      )}
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>{fmtListTime(c.last_message_at)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, marginTop: 3 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.last_dir === 'out' ? '↩ ' : ''}{resumenUltimo(c.last_type, c.last_body)}</span>
                    {fueraDelBot(c)
                      ? <span title="Se le mandó el seguimiento, no contestó y se cerró la ventana de 24 h. Para recuperarlo hay que escribirle por afuera."
                          style={{ fontSize: 10, fontWeight: 700, color: '#a855f7', flexShrink: 0, whiteSpace: 'nowrap' }}>Fuera del bot</span>
                      : flag && <span title={flag.text === 'Sin responder' ? 'Sin responder' : 'Para seguimiento: sin respuesta hace ' + flag.text} style={{ fontSize: 10, fontWeight: 700, color: flag.color, display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}><flag.icon size={11} /> {flag.text}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ===== Chat ===== */}
        {/* Mismo fondo que el listado de conversaciones: si no, el chat queda en otro tono */}
        <div style={{ flex: 1, display: isMobile && !selId ? 'none' : 'flex', flexDirection: 'column', minWidth: 0, background: 'var(--bg-secondary)' }}>
          {!conv ? (
            <div style={{ margin: 'auto', color: 'var(--text-muted)', fontSize: 14 }}>{selId ? 'Cargando...' : 'Elegí una conversación'}</div>
          ) : (
            <>
              {(() => {
                const badge = stateBadge(conv.state);
                const stateActions = (<>
                  {canWaWeb && <button className="btn btn-sm" onClick={openWaWeb} title="Abre WhatsApp Web con tu número y marca el lead como derivado a asesor" style={{ background: '#25D366', color: '#fff' }}><ExternalLink size={14} /> WhatsApp Web</button>}
                  {conv.state === 'bot' && <button className="btn btn-secondary btn-sm" onClick={() => changeState('human')}><HandMetal size={14} /> Tomar</button>}
                  {(conv.state === 'human' || conv.state === 'derived') && <button className="btn btn-ghost btn-sm" onClick={() => changeState('bot')}><Bot size={14} /> Devolver al bot</button>}
                  {conv.state === 'closed' && <button className="btn btn-secondary btn-sm" onClick={() => changeState(conv.status === 'derivado_asesor' ? 'derived' : 'bot')}><ArchiveRestore size={14} /> Desarchivar</button>}
                </>);
                const iconActions = (<>
                  <button className="btn btn-ghost btn-sm" onClick={toggleDetails} title={showDetails ? 'Ocultar detalles' : 'Ver detalles del lead'} style={{ padding: 6 }}>
                    {isMobile ? <UserRound size={18} /> : showDetails ? <PanelRightClose size={16} /> : <PanelRightOpen size={16} />}
                  </button>
                  {(<>
                    <button className="btn btn-ghost btn-sm" onClick={() => setMenu(menu === 'actions' ? null : 'actions')} style={{ padding: 6, position: 'relative', zIndex: 16 }}><MoreVertical size={16} /></button>
                    {menu === 'actions' && (
                      <div style={{ ...menuBox, right: 0, top: 36, minWidth: 190 }}>
                        {conv.state !== 'closed' && !conv.blocked && <button style={menuItem} onClick={() => changeState('closed')}><span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Archive size={13} /> Archivar conversación</span></button>}
                        {conv.blocked
                          ? <button style={menuItem} onClick={() => setBlocked(false)}><span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><ShieldCheck size={13} /> Desbloquear contacto</span></button>
                          : <button style={{ ...menuItem, color: 'var(--danger)' }} onClick={() => setBlocked(true)}><span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Ban size={13} /> Bloquear contacto</span></button>}
                        <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
                        <button style={{ ...menuItem, color: 'var(--danger)' }} onClick={() => removeConversation(false)}><span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Trash2 size={13} /> Borrar conversación</span></button>
                        <button style={{ ...menuItem, color: 'var(--danger)' }} onClick={() => removeConversation(true)}><span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Trash2 size={13} /> Borrar conversación y lead</span></button>
                      </div>
                    )}
                  </>)}
                </>);
                const name = (
                  <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, flex: 1 }}>
                    <ChannelIcon c={conv.channel} size={16} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conv.business_name || conv.external_id}</span>
                    {!isMobile && <span className="badge" style={{ fontSize: 10, background: badge.bg, color: badge.color, flexShrink: 0 }}>{badge.label}</span>}
                  </div>
                );
                if (isMobile) return (
                  // Mobile: fila 1 = volver + nombre + íconos; fila 2 = estado + acciones con texto
                  <div style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px 8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setSelId(null)} style={{ padding: 6, flexShrink: 0 }} title="Volver"><ChevronLeft size={20} /></button>
                      {name}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative', flexShrink: 0 }}>{iconActions}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', paddingLeft: 6, marginTop: 4 }}>
                      <span className="badge" style={{ fontSize: 11, background: badge.bg, color: badge.color }}>{badge.label}</span>
                      {stateActions}
                    </div>
                  </div>
                );
                return (
                  <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                    {name}
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', position: 'relative', flexShrink: 0 }}>{stateActions}{iconActions}</div>
                  </div>
                );
              })()}

              {/* Mensajes */}
              <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? 10 : 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(detail.messages || []).map(m => {
                  const out = m.direction === 'out';
                  const isAgent = m.sender === 'agent';
                  /* Una foto puede venir con un texto al lado, y ese texto es parte
                     del mensaje: mostrar solo la imagen lo hacía desaparecer. */
                  const pie = m.media_url && m.body && !/^\[.*\]$/.test(m.body) ? m.body : null;
                  const sinTranscribir = m.msg_type === 'audio' && !pie;
                  const texto = m.media_url ? pie
                    : (m.msg_type && m.msg_type !== 'text' && /^\[.*\]$/.test(String(m.body || ''))
                      ? resumenUltimo(m.msg_type, m.body) : m.body);
                  return (
                    <div key={m.id} style={{ alignSelf: out ? 'flex-end' : 'flex-start', maxWidth: isMobile ? '85%' : '75%', display: 'flex', flexDirection: 'column', alignItems: out ? 'flex-end' : 'flex-start', gap: 4 }}>
                      {m.media_url && (m.msg_type === 'audio' ? (
                        /* El audio queda escuchable aunque se haya transcripto bien:
                           es lo que deja comprobar la transcripción en vez de confiar. */
                        <audio src={m.media_url} controls preload="none" style={{ display: 'block', width: 260, maxWidth: '100%' }} />
                      ) : m.msg_type === 'video' ? (
                        /* Un video del material se mira acá mismo: mandarlo a otra pestaña
                           para cotizar una chatarra es una fricción que no hace falta. */
                        <video src={m.media_url} controls preload="metadata" style={{
                          display: 'block', maxWidth: '100%', maxHeight: 320, borderRadius: 12,
                          border: '1px solid var(--border)', background: '#000',
                        }} />
                      ) : (
                        /* La foto que mandó el cliente. El enlace viene firmado desde la API
                           y vence a las 12 horas: al refrescarse la pantalla llega uno nuevo. */
                        <a href={m.media_url} target="_blank" rel="noopener noreferrer" title={m.msg_type === 'sticker' ? 'Sticker' : 'Abrir la foto'}>
                          {/* Un sticker va chico y sin marco: es una reacción, no contenido */}
                          <img src={m.media_url} alt={m.msg_type === 'sticker' ? 'Sticker que mandó el cliente' : 'Foto que mandó el cliente'} style={{
                            display: 'block', maxWidth: '100%', objectFit: 'contain',
                            maxHeight: m.msg_type === 'sticker' ? 110 : 300,
                            borderRadius: 12,
                            ...(m.msg_type === 'sticker' ? {} : { border: '1px solid var(--border)', background: 'var(--bg-hover)' }),
                          }} />
                        </a>
                      ))}
                      {sinTranscribir && (
                        <div style={{ fontSize: 11.5, color: '#d97706', marginTop: 1 }}>No se entendió bien: escuchalo</div>
                      )}
                      {texto && (
                      <div style={{
                        padding: '8px 12px', borderRadius: 12, fontSize: 14, whiteSpace: 'pre-wrap',
                        background: out ? (isAgent ? '#22c55e' : 'var(--accent)') : 'var(--bg-hover)',
                        color: out ? '#fff' : 'var(--text-primary)',
                      }}>
                        {/* Un audio transcripto es texto igual que cualquier otro: sin la
                            marca no se distingue de lo que la persona escribió. */}
                        {m.msg_type === 'audio' && <span style={{ opacity: 0.7, fontWeight: 600 }}>🎤 </span>}
                        {texto}
                      </div>
                      )}
                      {/* La ubicación que mandó el cliente: el pin abre el mapa */}
                      {conUbicacion && m.msg_type === 'ubicación' && m.lat && m.lng && (
                        <a href={`https://www.google.com/maps?q=${encodeURIComponent(m.lat + ',' + m.lng)}`} target="_blank" rel="noopener noreferrer"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 12, fontSize: 13, fontWeight: 600, background: 'var(--bg-hover)', color: 'var(--accent)', border: '1px solid var(--border)' }}>
                          <MapPin size={15} /> Ver en el mapa{m.lugar_nombre && !m.lugar ? ' · ' + m.lugar_nombre : ''}
                        </a>
                      )}
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2, textAlign: out ? 'right' : 'left' }}>
                        {out ? (isAgent ? 'Vos' : 'Bot') : 'Cliente'} · {fmtTime(m.created_at)}
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              {/* Responder */}
              <div style={{ borderTop: '1px solid var(--border)', padding: '8px 12px 10px' }}>
                {notice && <div style={{ fontSize: 11, color: notice.color, marginBottom: 6 }}>{notice.text}</div>}
                {/* Mensajes prearmados: solo con la charla en manos de una persona, que es quien escribe */}
                {conListas && conv.state !== 'bot' && (hayListas.minorista || hayListas.mayorista) && !waBlocked && !sinVentana && (
                  <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                    {hayListas.minorista && <button className="btn btn-ghost btn-sm" onClick={() => pegarLista('minorista')} style={{ gap: 5, fontSize: 12 }}><ListOrdered size={14} /> Lista minorista</button>}
                    {hayListas.mayorista && <button className="btn btn-ghost btn-sm" onClick={() => pegarLista('mayorista')} style={{ gap: 5, fontSize: 12 }}><ListOrdered size={14} /> Lista mayorista</button>}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <textarea ref={textoRef} value={text} onChange={e => setText(e.target.value)} rows={conListas && text.includes('\n') ? 6 : 1} disabled={waBlocked || sinVentana}
                    placeholder={waBlocked ? 'Envío por WhatsApp pausado' : sinVentana ? 'Fuera de las 24 h: usá WhatsApp Web' : 'Escribí una respuesta...'}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                    style={{ flex: 1, resize: 'none', fontFamily: 'inherit', fontSize: isMobile ? 16 : 14 }} />
                  <button className="btn btn-primary" onClick={send} disabled={sending || !text.trim() || waBlocked || sinVentana}><Send size={16} /></button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ===== Detalles del lead ===== */}
        {conv && showDetails && (
          <div style={isMobile
            ? { position: 'fixed', inset: 0, zIndex: 1100, overflowY: 'auto', padding: 16, background: 'var(--bg-secondary)' }
            : { width: 290, borderLeft: '1px solid var(--border)', overflowY: 'auto', flexShrink: 0, padding: 14, background: 'var(--bg-secondary)' }}>
            {isMobile && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conv.business_name || conv.external_id}</div>
                <button className="btn btn-ghost btn-sm" onClick={() => setShowDetails(false)} style={{ padding: 6 }}><X size={20} /></button>
              </div>
            )}
            <div style={sectionTitle}>Lead</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
              {channelLabel(conv.channel)}{waPhone ? ' · ' + waPhone : ''}
            </div>
            <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>Estado</label>
            <select value={conv.status || ''} onChange={e => updateLead('status', e.target.value)} style={{ ...pill(sc.bg || 'var(--bg-hover)', sc.color || 'var(--text-primary)'), margin: '4px 0 10px' }}>
              {estadosPara(null, conv.status).map(([k, v]) => <option key={k} value={k} style={{ background: 'var(--bg-card)', color: 'var(--text-primary)' }}>{v.label}</option>)}
            </select>
            <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>Calificación</label>
            <select value={conv.qualification || ''} onChange={e => updateLead('qualification', e.target.value)} style={{ ...pill('var(--bg-hover)', qc.color || 'var(--text-primary)'), margin: '4px 0 10px' }}>
              {Object.entries(QUALIFICATION_CONFIG).map(([k, v]) => <option key={k} value={k} style={{ background: 'var(--bg-card)', color: 'var(--text-primary)' }}>{v.icon} {v.label}</option>)}
            </select>
            {conv.lead_id && conUbicacion && <>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>Ubicación</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '4px 0 10px' }}>
                <MapPin size={14} color="var(--text-muted)" />
                <input value={ubicacion} onChange={e => setUbicacion(e.target.value)} onBlur={guardarUbicacion} maxLength={100}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
                  placeholder="Localidad del cliente" style={{ flex: 1, fontSize: 13, padding: '6px 10px' }} />
              </div>
            </>}
            {conv.lead_id && conEtiquetas && <>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>Etiquetas</label>
              <div style={{ marginBottom: 12 }}><EtiquetasLead leadId={conv.lead_id} etiquetas={conv.tags || []} onCambio={() => loadDetail(selId)} /></div>
            </>}
            {conv.lead_id && <button className="btn btn-ghost btn-sm" onClick={() => { setLeadOpen(true); if (isMobile) setShowDetails(false); }} style={{ width: '100%', justifyContent: 'center', gap: 6, marginBottom: 14 }}><UserRound size={14} /> Ver ficha completa</button>}
            {canAnalysis && <AiAnalysis conv={conv} onUpdated={() => loadDetail(selId)} />}
          </div>
        )}
      </div>

      <div style={{ display: chatOpen ? 'none' : 'block' }}><UsageMeters compact onChange={setUsage} /></div>

      {leadOpen && conv?.lead_id && <LeadDetail leadId={conv.lead_id} onClose={() => { setLeadOpen(false); refreshAll(); }} />}
    </div>
  );
}
